/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-11-23 17:59:45
 * @LastEditors: Jack Deng
 * @LastEditTime: 2022-05-01 21:53:42
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorButton/slice/index.ts
 */
import {
  addDraggableCollapse,
  deleteDraggableCollapse,
} from '@/src/constrains/DraggableModules/reduxFunctions';
import { isCurrentPrimary } from '@/src/constrains/SalesChannelMultiLanguageSelect';
import {
  getSalesChannelContentButton,
  postSalesChannelContentButton,
} from '@/src/request/salesChannel/content';
import { randomNumber } from '@/src/tools/helper';
import { msg } from '@/src/tools/message';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import {
  generateDefaultState as _generateDefaultState,
  onCollapseOpen as _onCollapseOpen,
  assData,
  defaultStatus,
  hasError,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getGeneralFeaturedProduct as _getGeneralFeaturedProduct,
  getHasModified as _getHasModified,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
} from 'src/pages/SalesChannelContentEditor/slice/selectors'; //
import { scroll } from 'src/tools/helper';
import {
  ButtonType,
  convertDataFromServerToRedux,
  defaultOutlineButtonProps,
  defaultTextButtonProps,
  generateNewButton,
} from '../tool';

export const fetchButton = createAsyncThunk(
  'fetchSalesChannelContentButton',
  async (data: any) => {
    const { uuid, params } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getSalesChannelContentButton(uuid, params);
    return {
      response,
      isPrimary,
    };
  },
);

export const submitButton = createAsyncThunk(
  'submitSalesChannelCotentButton',
  async ({ body, params }: any) => {
    const response = await postSalesChannelContentButton(body, params);
    return {
      response,
    };
  },
);

export const getLoaded = () => {
  return _getLoaded(EnumReducers.button);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.button);
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.button, id);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.button, id);
};

export const getGeneralFeaturedProduct = () => {
  return _getGeneralFeaturedProduct(EnumReducers.button);
};

export const getState = () => {
  return _getState(EnumReducers.button);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.button);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.button);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.button);
};

export const getButtons = () => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.button].buttons,
    (v) => v,
  );
};

export const getButton = (uuid: any) => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.button].buttons,
    (list) => {
      const item = list.find((v) => v.uuid === uuid);
      return item;
    },
  );
};

const _defaultState = _generateDefaultState([]);

const defaultState = {
  ...defaultStatus,
  ..._defaultState,
  buttons: [],
};

const salesChannelContentButtonPage = createSlice({
  name: 'salesChannelContentButtonPage',
  initialState: defaultState,
  reducers: {
    resetData(state) {
      assData(state, defaultState);
    },
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      const id = idArr[0];
      scroll(`${id}-preview`);
      _onCollapseOpen(state, idArr);
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    onAddButton(state, action) {
      const id = randomNumber();
      const list = state.buttons;
      state.buttons.unshift({
        uuid: id,
        isNew: true,
        ...generateNewButton(`New Button ${list.length + 1}`),
      });
      addDraggableCollapse(state, id);
      state.hasModified = true;
    },
    onDuplicate(state, action) {
      const id = randomNumber();
      const { uuid } = action.payload;
      const index = state.buttons.findIndex((v) => v.uuid === uuid);
      state.buttons.unshift({
        ...state.buttons[index],
        uuid: id,
        isNew: true,
        name: `${state.buttons[index]?.name}-cloned-${
          state.buttons.length + 1
        }`,
      });
      addDraggableCollapse(state, id);
      state.hasModified = true;
    },
    onDelete(state, action) {
      const { uuid } = action.payload;
      const index = state.buttons.findIndex((v) => v.uuid === uuid);
      if (index !== -1) {
        state.buttons.splice(index, 1);
      }
      state.hasModified = true;
      deleteDraggableCollapse(state, uuid);
    },
    onChangeField(state, action) {
      const { uuid, path, value } = action.payload;
      const index = state.buttons.findIndex((v) => v.uuid === uuid);
      if (index !== -1) {
        set(state.buttons[index], path, value);
      }
      state.hasModified = true;
    },
    onResetButtonProp(state, action) {
      const { value, uuid } = action.payload;
      const index = state.buttons.findIndex((v) => v.uuid === uuid);
      const original = state.buttons[index];
      switch (value) {
        case ButtonType.OUTLINE_FILLED: {
          state.buttons[index] = {
            ...original,
            type: ButtonType.OUTLINE_FILLED,
            ...defaultOutlineButtonProps,
          };
          break;
        }
        case ButtonType.TEXT: {
          state.buttons[index] = {
            ...original,
            type: ButtonType.TEXT,
            ...defaultTextButtonProps,
          };
          break;
        }
      }
      state.hasModified = true;
    },
    onChangeFont(state, action) {
      const { uuid, path, value } = action.payload;
      const index = state.buttons.findIndex((v) => v.uuid === uuid);
      const original = state.buttons[index];
      if (index !== -1) {
        set(state.buttons[index], path, { ...get(original, path), ...value });
      }
      state.hasModified = true;
    },
    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchButton.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      assData(state, _data);
    });
    builder.addCase(submitButton.fulfilled, (state) => {
      state.hasModified = false;
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
    });
  },
});

export const {
  resetData,
  onErrorChange,
  onCollapseOpen,
  validation,
  onAddButton,
  onChangeField,
  onChangeFont,
  onResetButtonProp,
  onDuplicate,
  onDelete,
} = salesChannelContentButtonPage.actions;

export default salesChannelContentButtonPage.reducer;
